import React, { useEffect, useState } from "react";
import { ConfigurationSyncSummary } from "./CustomAiConfigurationForm";

const DOCUMENTS = Object.freeze([
  ["customAi", "Custom AI"],
  ["codex", "Codex"],
  ["eventEvaluation", "Event validation"],
]);

function enabledState(key, configuration) {
  if (key === "customAi" || key === "codex") return configuration.enabled === true;
  return Object.values(configuration.eventPolicies || {})
    .some((policy) => policy?.mode && policy.mode !== "OFF");
}

function formatTime(value) {
  if (!Number.isFinite(value) || value < 0) return "Unavailable";
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "medium",
    timeZone: "UTC",
  }) + " UTC";
}

function formatHealthTime(value) {
  if (!Number.isSafeInteger(value) || value < 0) return "Unavailable";
  return `${new Date(value).toLocaleString(undefined, { timeZone: "UTC" })} UTC`;
}

function formatAge(value) {
  if (!Number.isSafeInteger(value) || value <= 0) return "None";
  const seconds = Math.floor(value / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

const HEALTH_COMPONENTS = Object.freeze([
  ["java", "App backend · Event producer"],
  ["capture", "Windows chart capture"],
  ["webhook", "Webhook API"],
  ["evaluator", "Linux evaluator"],
]);

export function WorkerActivityPanel({ activity, stale }) {
  if (!activity) return <section className="adminHealthCard" aria-label="AI worker activity">
    <h3>AI workers</h3><p>Worker request tracking is not available on this server yet.</p>
  </section>;
  return (
    <section className="adminHealthCard" aria-label="AI worker activity">
      <div className="adminHealthCardHeader">
        <h3>AI workers</h3>
        <strong>{stale ? "Last known: " : ""}{activity.activeWorkers} active</strong>
      </div>
      <dl className="adminHealthMetrics">
        <div><dt>Requested work in last {Math.round(activity.windowMs / 1000)}s</dt><dd>{activity.requestingWorkers}</dd></div>
        <div><dt>Processing with a fresh heartbeat</dt><dd>{activity.processingWorkers}</dd></div>
        <div><dt>Workers seen</dt><dd>{activity.workers.length}</dd></div>
      </dl>
      <p>Busy workers request another job when a slot is free. Counts can overlap.</p>
      {activity.workers.length ? <div className="adminTableViewport">
        <table className="adminAuditTable" aria-label="Worker job requests">
          <thead><tr><th scope="col">Worker</th><th scope="col">IP address</th><th scope="col">Activity</th>
            <th scope="col">Last job request (UTC)</th></tr></thead>
          <tbody>{activity.workers.map((worker) => <tr key={worker.workerId}>
            <td>{worker.workerId}</td><td>{worker.ip || "Unavailable"}</td>
            <td>{worker.processing ? `Processing (${worker.activeLeases})` : worker.requesting ? "Requesting jobs" : "No recent requests"}</td>
            <td>{formatHealthTime(worker.lastClaimRequestAt)}</td>
          </tr>)}</tbody>
        </table>
      </div> : <p>No workers have requested jobs since webhooks started.</p>}
      <p>Activity refreshes every 15 seconds. Last-seen history resets when webhooks restarts.</p>
    </section>
  );
}

export function HealthStatusPanel({ api, active = true }) {
  const [health, setHealth] = useState(null);
  const [error, setError] = useState("");

  const refresh = React.useCallback(() => {
    if (!active) return Promise.resolve();
    setError("");
    return api.getHealth()
      .then(setHealth)
      .catch((healthError) => {
        if (healthError?.status !== 401) setError("Event Validation health could not be loaded.");
      });
  }, [active, api]);

  useEffect(() => {
    if (!active) return undefined;
    let mounted = true;
    const load = () => api.getHealth()
      .then((value) => { if (mounted) { setHealth(value); setError(""); } })
      .catch((healthError) => {
        if (mounted && healthError?.status !== 401) setError("Event Validation health could not be loaded.");
      });
    load();
    const timer = setInterval(load, 15_000);
    return () => { mounted = false; clearInterval(timer); };
  }, [active, api]);

  if (error && !health) return <div className="adminAlert adminAlertError" role="alert">{error}</div>;
  if (!health) return <p role="status">Loading Event Validation health…</p>;
  const counts = health.queue.counts;
  return (
    <div className="adminHealthPanel">
      <div className={`adminHealthSummary adminHealth${health.overall}`} role="status" aria-label="Event Validation health">
        <strong>Event Validation {health.overall}</strong>
        <span>Updated {formatHealthTime(health.generatedAt)}</span>
      </div>
      {error && <div className="adminAlert adminAlertWarning" role="alert">{error}</div>}
      <div className="adminHealthComponents">
        {HEALTH_COMPONENTS.map(([key, label]) => {
          const component = health.components[key];
          return (
            <section key={key} className={`adminHealthCard adminHealth${component.state}`} aria-label={label}>
              <div className="adminHealthCardHeader"><h3>{label}</h3><strong>{component.state}</strong></div>
              <dl className="adminHealthMetrics">
                <div><dt>Last heartbeat</dt><dd>{formatHealthTime(component.lastHeartbeatAt)}</dd></div>
                <div><dt>Last success</dt><dd>{formatHealthTime(component.lastSuccessAt)}</dd></div>
                <div><dt>Restart attempts</dt><dd>{component.restartAttempts}</dd></div>
              </dl>
              {component.lastError && <p className="adminHealthError">{component.lastError}</p>}
            </section>
          );
        })}
      </div>
      <section className={`adminHealthCard adminHealth${health.queue.state}`} aria-label="Event Validation queue">
        <div className="adminHealthCardHeader"><h3>Event Validation queue</h3><strong>{health.queue.state}</strong></div>
        <dl className="adminHealthMetrics adminHealthQueueMetrics">
          <div><dt>Pending capture</dt><dd>{counts.pendingCapture}</dd></div>
          <div><dt>Capture leased</dt><dd>{counts.captureLeased}</dd></div>
          <div><dt>Pending evaluation</dt><dd>{counts.pending}</dd></div>
          <div><dt>Evaluation leased</dt><dd>{counts.leased}</dd></div>
          <div><dt>Completed</dt><dd>{counts.completed}</dd></div>
          <div><dt>Failed</dt><dd>{counts.failed}</dd></div>
          <div><dt>Oldest capture</dt><dd>{formatAge(health.queue.oldestCaptureAgeMs)}</dd></div>
          <div><dt>Oldest evaluation</dt><dd>{formatAge(health.queue.oldestEvaluationAgeMs)}</dd></div>
        </dl>
        {health.queue.lastFailure && <p className="adminHealthError">
          Last error: {formatHealthTime(health.queue.lastFailedAt)} · {health.queue.lastFailure}
        </p>}
      </section>
      <button type="button" className="adminSecondaryButton" onClick={refresh}
        aria-label="Refresh Event Validation health">Refresh health</button>
    </div>
  );
}

export function SyncStatusPanel({ documents, syncStatus }) {
  return (
    <div className="adminStatusList">
      {DOCUMENTS.map(([key, label]) => (
        <section key={key} className="adminStatusSection">
          <h3>{label}</h3>
          <ConfigurationSyncSummary envelope={documents[key]} syncStatus={syncStatus?.[key]}
            enabled={enabledState(key, documents[key].configuration)} />
        </section>
      ))}
    </div>
  );
}

export function AuditPanel({ api, refreshVersion }) {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setError("");
    api.getAudit()
      .then((value) => { if (active) setEntries(value); })
      .catch((auditError) => {
        if (!active || auditError?.status === 401) return;
        setError("Administration audit could not be loaded. Refresh and try again.");
      });
    return () => { active = false; };
  }, [api, refreshVersion]);

  if (error) return <div className="adminAlert adminAlertError" role="alert">{error}</div>;
  if (!entries) return <p role="status">Loading audit…</p>;
  if (entries.length === 0) return <p>No administration activity has been recorded.</p>;
  return (
    <div className="adminTableViewport">
      <table className="adminAuditTable">
        <thead><tr><th>Time</th><th>Action</th><th>Administrator</th><th>Document</th><th>Revision</th><th>Source</th></tr></thead>
        <tbody>
          {[...entries].reverse().map((entry, index) => (
            <tr key={`${entry.at}-${entry.action}-${entry.revision ?? index}`}>
              <td>{formatTime(entry.at)}</td>
              <td>{entry.action}</td>
              <td>{entry.actorId ? "Administrator" : "System"}</td>
              <td>{entry.key ?? "—"}</td>
              <td>{entry.revision ?? "—"}</td>
              <td>{entry.source ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
